import { Cubit } from "blac-next";
import { ShippingAddressData } from "./ShippingAddressData";
import { AddressValidationResult } from "./addressValidationTypeAliases";
import {
  AddressesControllerService,
  AddressValidationRequest
} from "@9amhealth/openapi";
import reportErrorSentry from "src/lib/reportErrorSentry";

interface ShippingAddressValidationState {
  address?: ShippingAddressData;
  validationResult: AddressValidationResult | null;
  isLoading: boolean;
}

export class ShippingAddressValidationCubit extends Cubit<ShippingAddressValidationState> {
  constructor() {
    super({ validationResult: null, isLoading: false });
  }

  public emit(state: ShippingAddressValidationState) {
    // Any update that contains no `validationResult` should invalidate the last
    // result
    super.emit({
      ...{ validationResult: null },
      ...state
    });
  }

  /**
   * Validates an address without updating the state
   * @param address
   */
  public readonly validateAddressDirect = async (
    address: ShippingAddressData
  ) => {
    try {
      return (
        (
          (
            await AddressesControllerService.validateAddress(
              address as AddressValidationRequest
            )
          ).data as { result?: AddressValidationResult }
        ).result ?? null
      );
    } catch (e: unknown) {
      reportErrorSentry(e);
      return null;
    }
  };

  /**
   * Validates an address and updates the state accordingly.
   * @param address
   */
  public readonly validateAddress = async (address: ShippingAddressData) => {
    this.patch({ address, isLoading: true });

    const validationResult = await this.validateAddressDirect(address);

    // Always maintain a valid state - even if another request was fired parallelly
    // No need for error checks here, the result will be null in case of problems occurred
    this.patch({
      address,
      validationResult,
      isLoading: false
    });
  };
}
